import { useEffect, useMemo, useState } from "react";
import { Eye, EyeOff } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type {
  TerminalProviderId,
  TerminalSettings,
} from "@/lib/terminalSettings";

interface AiTerminalSettingsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  settings: TerminalSettings;
  onSave: (settings: TerminalSettings) => void;
}

/**
 * Runtime configuration for the AI terminal: API keys, models, the Ollama endpoint, the
 * default provider, and the system prompt. Edits live in a local draft and only commit on
 * Save, so Cancel (or closing) discards them.
 *
 * The dialog uses a `z-[…]` above the terminal's own stacking context because the terminal
 * (packages/react-terminal) is pinned just under the modal layer — see `.rt-root.ai-terminal-root`
 * in `src/styles.css`.
 */
export function AiTerminalSettings({
  open,
  onOpenChange,
  settings,
  onSave,
}: AiTerminalSettingsProps) {
  const [draft, setDraft] = useState<TerminalSettings>(settings);
  const [showKeys, setShowKeys] = useState(false);

  // Re-seed the draft from the live settings each time the dialog opens, so a cancelled edit
  // never bleeds into the next session of the dialog.
  useEffect(() => {
    if (open) setDraft(settings);
  }, [open, settings]);

  // Only providers that can actually run are selectable as the default (a cloud provider needs
  // a key; Ollama is always available). Mirrors the filtering in AiTerminal's buildProviders.
  const providerOptions = useMemo(() => {
    const opts: { id: TerminalProviderId; label: string }[] = [];
    if (draft.anthropic.apiKey.trim())
      opts.push({ id: "anthropic", label: "Claude (Anthropic)" });
    if (draft.openai.apiKey.trim())
      opts.push({ id: "openai", label: "OpenAI" });
    opts.push({ id: "ollama", label: "Ollama (local)" });
    if (draft.qwen.baseUrl.trim())
      opts.push({ id: "qwen", label: "Qwen (local)" });
    return opts;
  }, [draft.anthropic.apiKey, draft.openai.apiKey, draft.qwen.baseUrl]);

  const activeValue: TerminalProviderId = providerOptions.some(
    (o) => o.id === draft.activeProviderId,
  )
    ? draft.activeProviderId
    : providerOptions[providerOptions.length - 1].id;

  const keyType = showKeys ? "text" : "password";

  const handleSave = () => onSave({ ...draft, activeProviderId: activeValue });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[2147483647] max-w-lg">
        <DialogHeader>
          <DialogTitle>AI terminal settings</DialogTitle>
          <DialogDescription>
            Keys are stored in this browser only and used to call the provider
            directly from your machine — nothing is sent to a
            Gridd/DeckAI/SlideCraft server.
          </DialogDescription>
        </DialogHeader>

        <div className="grid max-h-[60vh] gap-5 overflow-y-auto py-2 pr-1">
          {/* Default provider + key visibility */}
          <div className="grid gap-2">
            <Label htmlFor="ai-active-provider">Default provider</Label>
            <Select
              value={activeValue}
              onValueChange={(v) =>
                setDraft((d) => ({
                  ...d,
                  activeProviderId: v as TerminalProviderId,
                }))
              }
            >
              <SelectTrigger id="ai-active-provider">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="z-[2147483647]">
                {providerOptions.map((o) => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Add a key below to enable a cloud provider. You can also switch
              providers live from the terminal header.
            </p>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">API keys &amp; models</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowKeys((s) => !s)}
            >
              {showKeys ? <EyeOff /> : <Eye />}
              {showKeys ? "Hide keys" : "Show keys"}
            </Button>
          </div>

          {/* Claude */}
          <section className="grid gap-3">
            <h3 className="text-sm font-semibold">Claude (Anthropic)</h3>
            <div className="grid gap-2">
              <Label htmlFor="ai-anthropic-key">API key</Label>
              <Input
                id="ai-anthropic-key"
                type={keyType}
                autoComplete="off"
                placeholder="sk-ant-…"
                value={draft.anthropic.apiKey}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    anthropic: { ...d.anthropic, apiKey: e.target.value },
                  }))
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="ai-anthropic-model">Model</Label>
              <Input
                id="ai-anthropic-model"
                autoComplete="off"
                value={draft.anthropic.model}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    anthropic: { ...d.anthropic, model: e.target.value },
                  }))
                }
              />
            </div>
          </section>

          <Separator />

          {/* OpenAI */}
          <section className="grid gap-3">
            <h3 className="text-sm font-semibold">OpenAI</h3>
            <div className="grid gap-2">
              <Label htmlFor="ai-openai-key">API key</Label>
              <Input
                id="ai-openai-key"
                type={keyType}
                autoComplete="off"
                placeholder="sk-…"
                value={draft.openai.apiKey}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    openai: { ...d.openai, apiKey: e.target.value },
                  }))
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="ai-openai-model">Model</Label>
              <Input
                id="ai-openai-model"
                autoComplete="off"
                value={draft.openai.model}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    openai: { ...d.openai, model: e.target.value },
                  }))
                }
              />
            </div>
          </section>

          <Separator />

          {/* Ollama — keyless local model */}
          <section className="grid gap-3">
            <h3 className="text-sm font-semibold">Ollama (local)</h3>
            <div className="grid gap-2">
              <Label htmlFor="ai-ollama-url">Base URL</Label>
              <Input
                id="ai-ollama-url"
                autoComplete="off"
                placeholder="http://localhost:11434"
                value={draft.ollama.baseUrl}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    ollama: { ...d.ollama, baseUrl: e.target.value },
                  }))
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="ai-ollama-model">Model</Label>
              <Input
                id="ai-ollama-model"
                autoComplete="off"
                value={draft.ollama.model}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    ollama: { ...d.ollama, model: e.target.value },
                  }))
                }
              />
            </div>
          </section>

          <Separator />

          {/* Qwen via Docker Model Runner — OpenAI-compatible, keyless */}
          <section className="grid gap-3">
            <h3 className="text-sm font-semibold">
              Qwen (Docker Model Runner)
            </h3>
            <div className="grid gap-2">
              <Label htmlFor="ai-qwen-url">Base URL</Label>
              <Input
                id="ai-qwen-url"
                autoComplete="off"
                placeholder="/__dmr/engines/v1"
                value={draft.qwen.baseUrl}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    qwen: { ...d.qwen, baseUrl: e.target.value },
                  }))
                }
              />
              <p className="text-xs text-muted-foreground">
                In the browser this is a same-origin path reverse-proxied to
                Docker Model Runner (the dev server) — Model Runner sends no
                CORS headers, so it cannot be called directly. In the desktop
                app it is the direct
                <code> http://localhost:12434/engines/v1</code>, which the shell
                calls from its main process. Either default is filled in for
                you; change it only if you front Model Runner yourself.
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="ai-qwen-model">Model</Label>
              <Input
                id="ai-qwen-model"
                autoComplete="off"
                placeholder="ai/qwen3.6"
                value={draft.qwen.model}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    qwen: { ...d.qwen, model: e.target.value },
                  }))
                }
              />
              <p className="text-xs text-muted-foreground">
                The id as Docker Model Runner namespaces it — run
                <code> docker model ls</code> to see what is pulled on this
                machine. A model that is not pulled answers with a 404, which
                looks like a broken terminal rather than a missing model.
              </p>
            </div>
          </section>

          <Separator />

          {/* System prompt */}
          <div className="grid gap-2">
            <Label htmlFor="ai-system-prompt">System prompt</Label>
            <Textarea
              id="ai-system-prompt"
              rows={4}
              value={draft.systemPrompt}
              onChange={(e) =>
                setDraft((d) => ({ ...d, systemPrompt: e.target.value }))
              }
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
